import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  JSONExport,
  JSONExportWithTimestamp,
  MotifGeneratorType,
} from '../components/MotifPatternGenerator/types'
import { toJpeg } from 'html-to-image'
import { useEnv } from '../utils/useEnv'
import { useContextPanels } from './panelsContext'
import { useContextSnackbar } from './snackbarContext'
import { useAxiosApi } from '../utils/axios'
import { Errors } from '../constants/apiErrors'
import { AxiosError } from 'axios'
import { saveAs } from 'file-saver'
import { Motif } from '../types/motifs'
import { PositionXY } from '../types/PositionXY'
import { nanoid } from 'nanoid'
import { FreeLayoutGeneratorState } from '../types/GeneratorState'
import { ExportLayoutModalResponse } from '../components/MotifPatternGenerator/ExportLayoutModal'

interface GeneratorContextType {
  selectedGeneratorType: MotifGeneratorType
  selectGeneratorType: (generatorType: MotifGeneratorType) => void
  exportInvoked: boolean
  setExportInvoked: Dispatch<SetStateAction<boolean>>
  exportModal: boolean
  setExportModal: Dispatch<SetStateAction<boolean>>
  startExport: boolean
  setStartExport: Dispatch<SetStateAction<boolean>>
  exportResponse: ExportLayoutModalResponse | null
  isPatternGenerated: boolean
  setIsPatternGenerated: Dispatch<SetStateAction<boolean>>
  exportGeneratorCoordinatesToJSON: (json: JSONExport) => void
  handleExport: (element: HTMLElement) => Promise<boolean>
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  isRegenerated: boolean
  setIsRegenerated: Dispatch<SetStateAction<boolean>>
  handleExportCanvas: (canvasElement: HTMLCanvasElement) => void
  selectedMotifForFreeGeneration: Motif | undefined
  setSelectedMotifForFreeGeneration: Dispatch<SetStateAction<Motif | undefined>>
  freeGeneratorTouchEventPageXY: PositionXY | undefined
  setFreeGeneratorTouchEventPageXY: Dispatch<SetStateAction<PositionXY | undefined>>
  freeLayoutGeneratorFeatureDiscoveryAlreadyShown: boolean
  setFreeLayoutGeneratorFeatureDiscoveryAlreadyShown: Dispatch<SetStateAction<boolean>>
  freeLayoutGeneratorState: FreeLayoutGeneratorState | undefined
  setFreeLayoutGeneratorState: Dispatch<SetStateAction<FreeLayoutGeneratorState | undefined>>
  qrCodeLink: string
  setQrCodeLink: Dispatch<SetStateAction<string>>
}

interface Props {
  children: ReactNode
}

export const EXPORT_SIZE = 1000
const Context = createContext({} as GeneratorContextType)

const MotifGeneratorContext = ({ children }: Props) => {
  const { isKiosk } = useEnv()
  const { openQrModal } = useContextPanels()
  const { showSnackbar } = useContextSnackbar()
  const { post } = useAxiosApi()
  const [selectedGeneratorType, setSelectedGeneratorType] = useState<MotifGeneratorType>(
    MotifGeneratorType.CIRCLE,
  )
  const [exportInvoked, setExportInvoked] = useState<boolean>(false)
  const [exportModal, setExportModal] = useState<boolean>(false)
  const [startExport, setStartExport] = useState<boolean>(false)
  const [exportResponse] = useState<ExportLayoutModalResponse | null>(null)
  const [isPatternGenerated, setIsPatternGenerated] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [isRegenerated, setIsRegenerated] = useState<boolean>(false)
  const [selectedMotifForFreeGeneration, setSelectedMotifForFreeGeneration] = useState<Motif>()
  const [freeGeneratorTouchEventPageXY, setFreeGeneratorTouchEventPageXY] = useState<PositionXY>()
  const [
    freeLayoutGeneratorFeatureDiscoveryAlreadyShown,
    setFreeLayoutGeneratorFeatureDiscoveryAlreadyShown,
  ] = useState(false)
  const [freeLayoutGeneratorState, setFreeLayoutGeneratorState] =
    useState<FreeLayoutGeneratorState>()
  const [qrCodeLink, setQrCodeLink] = useState<string>('')

  const selectGeneratorType = (generatorType: MotifGeneratorType) => {
    setSelectedGeneratorType(generatorType)
  }

  const forceIdRef = useRef<string>(nanoid())
  useEffect(() => {
    if (exportInvoked) {
      forceIdRef.current = nanoid()
    }
  }, [exportInvoked])

  const exportGeneratorCoordinatesToJSON = async (json: JSONExport) => {
    try {
      const jsonWithTime: JSONExportWithTimestamp = {
        timestamp: Number(Date.now()),
        timezone: new Date().getTimezoneOffset(),
        ...json,
      }

      const res = await post(`/uploadJson?forceId=${forceIdRef.current}`, jsonWithTime)
      return res.data
    } catch (e) {
      console.log(e)
    }
  }

  const handleExport = async (element: HTMLElement): Promise<boolean> => {
    setLoading(true)
    if (isKiosk) {
      const sizeMultiplier = exportResponse === ExportLayoutModalResponse.TILES ? 3 : 1
      const dataUrl = await toJpeg(element, {
        width: (EXPORT_SIZE / window.devicePixelRatio) * sizeMultiplier,
        height: (EXPORT_SIZE / window.devicePixelRatio) * sizeMultiplier,
        pixelRatio: 3,
        quality: 0.85,
        skipAutoScale: true,
      })
      const blob = await fetch(dataUrl).then(res => res.blob())
      if (!blob) throw new Error('Could not create Blob from image')
      const imageUrl = await uploadImage(blob)
      setQrCodeLink(imageUrl)
      setLoading(false)
      // Open QR modal after export completes in kiosk mode
      openQrModal()
    } else {
      try {
        let sizeMultiplier = exportResponse === ExportLayoutModalResponse.TILES ? 3 : 3
        const dataUrl = await toJpeg(element, {
          width: (EXPORT_SIZE / window.devicePixelRatio) * sizeMultiplier,
          height: (EXPORT_SIZE / window.devicePixelRatio) * sizeMultiplier,
          skipAutoScale: true,
          quality: 0.92,
        })

        // Safari/iOS compatible download approach
        const uniqueId = nanoid(6)
        const filename = `pattern_${uniqueId}.jpg`

        // Convert dataURL to blob for better Safari compatibility
        const blob = await fetch(dataUrl).then(res => res.blob())

        // Use file-saver for cross-browser compatibility (especially Safari)
        saveAs(blob, filename)
      } catch (e) {
        console.error('Export failed:', e)
        showSnackbar('snackbarMessages.exportError', 5)
      } finally {
        setLoading(false)
      }
    }
    return Promise.resolve(true)
  }

  const handleExportCanvas = async (canvasElement: HTMLCanvasElement) => {
    canvasElement.toBlob(async blob => {
      try {
        if (!blob) return
        const uniqueId = nanoid(6)
        const filename = `pattern_${uniqueId}.jpg`
        saveAs(blob, filename)
        if (isKiosk) {
          await uploadImage(blob)
        }
      } catch (e) {
        let errorKey = Errors.SERVER_ERROR.toString()
        const message = (e as AxiosError)?.response?.data?.message
        if (message && Object.values(Errors).includes(message)) {
          errorKey = message
        }
        showSnackbar(`snackbarMessages.uploadApiError.${errorKey}`, 10)
        console.dir(e)
      } finally {
        setLoading(false)
      }
    }, 'image/jpeg')
  }

  const uploadImage = async (blob: Blob): Promise<string> => {
    try {
      const formData = new FormData()
      formData.append('image', blob, 'pattern.jpeg')
      const res = await post(`/upload?forceId=${forceIdRef.current}`, formData)

      // NOTE: axios does unsolicited json parsing
      const json = res.data as { url: string }
      return json.url
    } catch (e) {
      let errorKey = Errors.SERVER_ERROR.toString()
      const message = (e as AxiosError)?.response?.data?.message
      if (message && Object.values(Errors).includes(message)) {
        errorKey = message
      }
      showSnackbar(`snackbarMessages.uploadApiError.${errorKey}`, 10)
      console.dir(e)
      throw e
    } finally {
      setLoading(false)
    }
  }

  return (
    <Context.Provider
      value={{
        selectedGeneratorType,
        selectGeneratorType,
        exportInvoked,
        setExportInvoked,
        exportModal,
        setExportModal,
        startExport,
        setStartExport,
        exportResponse,
        isPatternGenerated,
        setIsPatternGenerated,
        exportGeneratorCoordinatesToJSON,
        handleExport,
        loading,
        setLoading,
        isRegenerated,
        setIsRegenerated,
        handleExportCanvas,
        selectedMotifForFreeGeneration,
        setSelectedMotifForFreeGeneration,
        freeGeneratorTouchEventPageXY,
        setFreeGeneratorTouchEventPageXY,
        freeLayoutGeneratorFeatureDiscoveryAlreadyShown,
        setFreeLayoutGeneratorFeatureDiscoveryAlreadyShown,
        freeLayoutGeneratorState,
        setFreeLayoutGeneratorState,
        qrCodeLink,
        setQrCodeLink,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useContextMotifGenerator = () => {
  return useContext(Context)
}

export default MotifGeneratorContext
